/*
 * Fancy Froggy - colour swatches on product cards.
 *
 * Swaps the card photo and retargets the card's links when a swatch is chosen,
 * so shoppers can see every colour without leaving the grid.
 *
 * Handles every card style on the site:
 *   .card-wrapper  - the stock collection card. Its whole surface is a link,
 *                    made by stretching the title link over the card with an
 *                    ::after overlay (the card is no longer wrapped in an <a>,
 *                    because Add to cart and its option picker cannot sit
 *                    inside a link). The swatch row is raised above that
 *                    overlay, and clicks on swatches are cancelled so they
 *                    never open the product.
 *   .ffcs-card     - the character shop card, which has separate links on the
 *                    photo, the title and the button. All of them get retargeted.
 *   .wg__card      - the wiggly grid card, built the same way as the shop card.
 *
 * The cards handle their hover photo differently, which matters when a
 * swatch is chosen:
 *   - the shop and wiggly cards overlay their hover image on top of the main
 *     one, so the main photo is always visible underneath;
 *   - the stock card fades the FIRST image out as the second fades in.
 * Hiding the second image is therefore not enough on the stock card - the
 * first has to be pinned visible too, or a card left under the cursor shows
 * nothing at all. See the .ff-swatch-chosen rules below.
 *
 * Choosing a swatch also preselects that colour in the card's Add to cart
 * picker; assets/ff-quick-add.js listens for the same clicks.
 *
 * The stylesheet lives in here rather than a .css file so the whole feature is
 * one asset. Guarded so it is harmless if the tag is included more than once.
 */
(function () {
  if (window.ffCardSwatchesReady) return;
  window.ffCardSwatchesReady = true;

  var CSS = [
    '.ff-swatches{display:flex;flex-wrap:wrap;align-items:center;gap:.6rem;margin:.8rem 0 .2rem;line-height:1}',
    '.ff-swatches--center{justify-content:center}',
    '.ff-swatches--right{justify-content:flex-end}',
    '.ff-swatch{position:relative;display:inline-block;width:2.2rem;height:2.2rem;border-radius:50%;background-color:var(--color-background,#fff);background-size:cover;background-position:center;cursor:pointer;box-shadow:0 0 0 1px rgba(var(--color-text-rgb,0,0,0),.28);transition:transform .15s ease,box-shadow .15s ease}',
    '.ff-swatch:hover{transform:scale(1.14)}',
    '.ff-swatch.is-active{box-shadow:0 0 0 2px var(--color-background,#fff),0 0 0 4px var(--color-accent,#ed5796)}',
    '.ff-swatch:focus-visible{outline:2px solid var(--color-accent,#ed5796);outline-offset:3px}',
    '.ff-swatch--soldout{opacity:.45}',
    '.ff-swatch--soldout::after{content:"";position:absolute;inset:0;border-radius:50%;background:linear-gradient(to top left,transparent 46%,currentColor 46%,currentColor 54%,transparent 54%)}',
    '.ff-swatch--more{width:auto;min-width:2.2rem;padding:0 .6rem;border-radius:1.1rem;box-shadow:none;opacity:.7;font-size:1.2rem;display:inline-flex;align-items:center;justify-content:center;cursor:default}',
    '.ff-swatch--more:hover{transform:none}',
    /* Stock card: keep the swatch row above the title link's full-card overlay. */
    '.card-wrapper .ff-swatches{position:relative;z-index:2}',
    /* Stock card: pin the main photo visible, since its hover effect fades it
       out rather than covering it. Without this, a card sitting under the
       cursor goes blank the moment a swatch is clicked. */
    '.card__media.ff-swatch-chosen .media--hover-effect > img:first-child{opacity:1!important}',
    '.card__media.ff-swatch-chosen .media--hover-effect > img + img{display:none!important}',
    /* Shop and wiggly cards: the hover image sits on top, so removing it is enough. */
    '.ffcs-card .ff-swatches,.wg__card .ff-swatches{margin:-.2rem 0 1.2rem}',
    '.wg__card .ff-swatches--empty{height:2rem}',
    '.ffcs-card .ff-swatch,.wg__card .ff-swatch{width:2rem;height:2rem;box-shadow:0 0 0 1px rgba(0,0,0,.18)}',
    '.ffcs-card .ff-swatch.is-active{box-shadow:0 0 0 2px #fff,0 0 0 4px var(--ring-text,#ed5796)}',
    '.wg__card .ff-swatch.is-active{box-shadow:0 0 0 2px #fff,0 0 0 4px var(--ffqa-accent,#ed5796)}',
    '.ffcs-card.ff-swatch-chosen .ffcs-hover,.wg__card.ff-swatch-chosen .wg__hover{display:none!important}',
    '@media (prefers-reduced-motion:reduce){.ff-swatch{transition:none}.ff-swatch:hover{transform:none}}'
  ].join('');

  function injectCSS() {
    if (document.getElementById('ff-card-swatches-css')) return;
    var style = document.createElement('style');
    style.id = 'ff-card-swatches-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function setHref(el, url) {
    if (el && url) el.setAttribute('href', url);
  }

  function chooseSwatch(swatch) {
    var group = swatch.parentElement;
    if (!group) return;

    var ffcsCard = swatch.closest('.ffcs-card');
    var wgCard = ffcsCard ? null : swatch.closest('.wg__card');
    var stockCard = (ffcsCard || wgCard) ? null : swatch.closest('.card-wrapper');
    var scope = ffcsCard || wgCard || stockCard || group;

    var image = scope.querySelector('.ffcs-imgwrap img:not(.ffcs-hover)') ||
                scope.querySelector('.wg__imgwrap img:not(.wg__hover)') ||
                scope.querySelector('.card__media img');

    var src = swatch.getAttribute('data-image-src');
    if (image && src) {
      image.setAttribute('src', src);

      var srcset = swatch.getAttribute('data-image-srcset');
      if (srcset) image.setAttribute('srcset', srcset);

      var alt = swatch.getAttribute('data-image-alt');
      if (alt) image.setAttribute('alt', alt);

      if (ffcsCard || wgCard) {
        (ffcsCard || wgCard).classList.add('ff-swatch-chosen');
      } else {
        var media = image.closest('.card__media');
        if (media) media.classList.add('ff-swatch-chosen');
      }
    }

    var url = swatch.getAttribute('data-variant-url');
    if (url) {
      if (stockCard) {
        setHref(stockCard.querySelector('.card-information .card__heading a'), url);
      }
      if (ffcsCard) {
        setHref(ffcsCard.querySelector('.ffcs-imgwrap'), url);
        setHref(ffcsCard.querySelector('.ffcs-cardtitle a'), url);
        setHref(ffcsCard.querySelector('a.ffcs-viewbtn'), url);
      }
      if (wgCard) {
        setHref(wgCard.querySelector('.wg__imgwrap'), url);
        setHref(wgCard.querySelector('.wg__title a'), url);
        setHref(wgCard.querySelector('a.wg__btn'), url);
      }
    }

    var siblings = group.querySelectorAll('.ff-swatch');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('is-active');
      if (siblings[i].hasAttribute('aria-pressed')) {
        siblings[i].setAttribute('aria-pressed', 'false');
      }
    }

    swatch.classList.add('is-active');
    swatch.setAttribute('aria-pressed', 'true');
  }

  function findSwatch(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var swatch = target.closest('.ff-swatch');
    if (!swatch) return null;
    if (swatch.classList.contains('ff-swatch--more')) return null;
    return swatch;
  }

  document.addEventListener('click', function (event) {
    var swatch = findSwatch(event.target);
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    chooseSwatch(swatch);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var swatch = findSwatch(event.target);
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    chooseSwatch(swatch);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectCSS);
  } else {
    injectCSS();
  }
})();